import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Mail, ArrowRight, PlayCircle, CheckCircle, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AnimatedSection, AnimatedCard } from '../components/AnimatedSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OptInPage = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!firstName.trim()) {
      toast.error('Please enter your first name');
      return;
    }
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      // Send to MailerLite via backend
      const response = await axios.post(`${API}/subscribers/subscribe`, {
        email_address: email,
        first_name: firstName.trim()
      }, {
        timeout: 10000
      });

      if (response.data.success) {
        // Store email in localStorage for reference
        localStorage.setItem('userEmail', email);
        
        // Track opt-in conversion in Google Analytics
        if (window.gtag) {
          window.gtag('event', 'generate_lead', {
            'event_category': 'engagement',
            'event_label': 'Email Opt-in',
            'value': 1
          });
        }
        
        toast.success('Success! Redirecting you now...');
        
        // Redirect to VSL page
        setTimeout(() => {
          navigate('/breakdown');
        }, 1500);
      } else {
        toast.error(response.data.message || 'Failed to subscribe. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      let errorMessage = 'An error occurred. Please try again later.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 422) {
        errorMessage = 'Invalid email address format';
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please check your connection and try again.';
      } else if (!error.response) {
        errorMessage = 'Unable to connect. Please check your internet connection.';
      }
      
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  const testimonials = [
    {
      name: "Matt",
      image: "https://customer-assets.emergentagent.com/job_working-men-gains/artifacts/842gjd54_matt%20transformation.png",
      alt: "Matt transformation - before and after"
    },
    {
      name: "Jack",
      image: "https://customer-assets.emergentagent.com/job_working-men-gains/artifacts/wotcn5uh_jack%20testimonial.jpeg",
      alt: "Jack transformation - before and after"
    },
    {
      name: "Mario",
      image: "https://customer-assets.emergentagent.com/job_working-men-gains/artifacts/okv01bvp_Mario%20transformation%20.jpeg",
      alt: "Mario transformation - before and after"
    }
  ];

  const scrollToForm = () => {
    document.getElementById('email-form').scrollIntoView({ 
      behavior: 'smooth' 
    });
  };

  return (
    <div className="optin-page">
      {/* Hero Section */}
      <section className="optin-hero-section">
        <div className="optin-hero-content">
          <div className={`optin-hero-text ${isVisible ? 'fade-in' : ''}`}>
            <h1 className="optin-headline">
              We Help Men Working Long Hours Get Lean, Strong, and Visibly Muscular Within 3 Months With a Plan Built Around Their Schedule
            </h1>
            <p className="optin-subheadline">
              See how busy tradesmen, FIFO workers, and shift workers are finally building the body their effort deserves without cutting out the foods they enjoy.
            </p>
          </div>

          {/* Email Form */}
          <form 
            id="email-form"
            onSubmit={handleSubmit} 
            className={`email-form ${isVisible ? 'slide-up' : ''}`}
          >
            <div className="email-input-wrapper">
              <User className="email-icon" size={20} />
              <Input
                type="text"
                placeholder="Enter your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="email-input"
                disabled={isLoading}
                required
              />
            </div>
            <div className="email-input-wrapper">
              <Mail className="email-icon" size={20} />
              <Input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="email-input"
                disabled={isLoading}
                required
              />
            </div>
            <Button 
              type="submit"
              className="optin-cta-button"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Show Me How This Works'}
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <p className="privacy-text">
              <CheckCircle size={16} className="check-icon" />
              We respect your privacy. Unsubscribe at any time.
            </p>
          </form>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="optin-testimonials-section">
        <div className="optin-testimonials-container">
          <AnimatedSection animation="fadeUp">
            <h2 className="optin-section-headline">Real Results From Men Working Brutal Hours</h2>
          </AnimatedSection>
          
          <div className="optin-testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <AnimatedCard 
                key={index} 
                index={index}
                className="optin-testimonial-card-wrapper"
              >
                <Card className="optin-testimonial-card">
                  <div className="optin-testimonial-image-wrapper">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.alt}
                      className="optin-testimonial-image"
                    />
                  </div>
                </Card>
              </AnimatedCard>
            ))}
          </div>

          {/* Talking Testimonial Video */}
          <AnimatedSection animation="fadeUp" delay={0.2}>
            <div className="optin-talking-testimonial-container">
              <h3 className="optin-talking-testimonial-title">Hear From Our Clients</h3>
              <div className="optin-talking-video-wrapper">
                <video 
                  className="optin-testimonial-video"
                  controls
                  playsInline
                  preload="metadata"
                  poster="/zac-testimonial-thumbnail.jpg"
                >
                  <source 
                    src="https://customer-assets.emergentagent.com/job_working-men-gains/artifacts/circr1a0_Zac%20Testimonial.MP4" 
                    type="video/mp4" 
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection animation="fadeUp" delay={0.3}>
            <Button 
              onClick={scrollToForm}
              className="optin-cta-button-secondary"
              size="lg"
            >
              Show Me How This Works
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="optin-footer">
        <div className="optin-footer-content">
          <h3 className="optin-footer-brand">Rudi Garcia Coaching</h3>
          <p className="optin-footer-disclaimer">
            This site is not a part of the Facebook website or Facebook Inc. Additionally, 
            this site is not endorsed by Facebook in any way. Facebook is a trademark of Facebook, Inc.
          </p>
          <p className="optin-footer-copyright">
            Copyright Rudi Garcia Coaching | All Rights Reserved
          </p>
        </div>
      </footer>
    </div>
  );
};

export default OptInPage;
